import type { ByecodeSize } from '@byecode/ui'
import { Flex, Tooltip } from '@byecode/ui'
import type { ValueVariable } from '@lighthouse/core'
import { type Field, type VariableADTvalue, VariableType } from '@lighthouse/core'
import { safeToArray, safeToNumber, safeToString } from '@lighthouse/tools'
import React, { useId, useMemo, useRef } from 'react'

import { getRealField, isDateValue, scientificNotationToString } from '../../../../utils'
import { CheckboxItemPreviewer } from '../../../FieldValuePreviewer/CheckboxValuePreviewer'
import { Placeholder } from '../../../PlaceHolder'
import { TagIcon } from '../../../TagIcon'
import { AggregationInnerTypeToFieldType, DEFAULT_VALUE_VARIABLE } from '../../constant'
import DateInput from './DateInput'
import { FileInput } from './FileInput'
import { SelectInput } from './SelectInput'
import * as SC from './styles'
import { TextInput } from './TextInput'

interface VariablePreviewProps {
    size?: ByecodeSize
    value?: ValueVariable
    placeholder?: string
    field?: Field
    disabled?: boolean
    width?: string
    opened?: boolean
    filePercent?: number
    prefix?: React.ReactNode
    focusOutLine?: boolean
    onChange: (value: VariableADTvalue) => void
    onChangeOpen: () => void
    onCancelUpload: () => void
}

export const VariablePreview: React.FunctionComponent<VariablePreviewProps> = props => {
    const { size, value: data, placeholder, field, disabled, width, opened, filePercent, prefix, focusOutLine, onChangeOpen, onChange, onCancelUpload } = props
    const id = useId()

    const realField = useMemo(() => (field ? getRealField(field) : field), [field])

    const fieldType = useMemo(() => {
        if (!realField) {
            return
        }
        if (realField?.type === 'aggregation' && realField.innerType) {
            return AggregationInnerTypeToFieldType[realField.innerType]
        }
        return realField?.type
    }, [realField])
    const fieldTypeRef = useRef(fieldType)
    fieldTypeRef.current = fieldType

    return useMemo(() => {
        const variableValue = data?.valueVariable?.value

        switch (fieldType) {
            case 'id':
            case 'text':
            case 'email':
            case 'url':
            case 'phoneNumber':
            case 'formula':
            case 'runtime': {
                return (
                    <TextInput
                        size={size}
                        data={variableValue === undefined ? variableValue : safeToString(variableValue)}
                        placeholder={placeholder ?? (fieldType === 'url' ? '请输入网页链接' : undefined)}
                        width={width}
                        disabled={disabled}
                        prefix={prefix}
                        focusOutLine={focusOutLine}
                        onBlur={e =>
                            onChange({
                                type: VariableType.VALUE,
                                valueVariable: {
                                    type: fieldType === 'runtime' ? 'text' : fieldType,
                                    value: e.target.value
                                }
                            })
                        }
                        onUnmountBlur={v => {
                            // 字段类型已经切走时，不要把旧输入写回新类型
                            if (fieldTypeRef.current !== fieldType) {
                                return
                            }
                            onChange({
                                type: VariableType.VALUE,
                                valueVariable: {
                                    type: fieldType === 'runtime' ? 'text' : fieldType,
                                    value: v
                                }
                            })
                        }}
                        onChangeOpen={onChangeOpen}
                    />
                )
            }
            case 'number': {
                return (
                    <TextInput
                        size={size}
                        type="number"
                        data={variableValue === undefined ? variableValue : safeToNumber(variableValue)}
                        placeholder={placeholder}
                        width={width}
                        disabled={disabled}
                        prefix={prefix}
                        focusOutLine={focusOutLine}
                        // onChange={e =>
                        //     onChange({
                        //         type: VariableType.VALUE,
                        //         valueVariable: {
                        //             type: fieldType,
                        //             value: scientificNotationToString(e.target.value)
                        //         }
                        //     })
                        // }
                        onBlur={e => {
                            onChange({
                                type: VariableType.VALUE,
                                valueVariable: {
                                    type: fieldType,
                                    value: scientificNotationToString(e.target.value)
                                }
                            })
                        }}
                        onUnmountBlur={v => {
                            if (fieldTypeRef.current !== fieldType) {
                                return
                            }
                            onChange({
                                type: VariableType.VALUE,
                                valueVariable: {
                                    type: fieldType,
                                    value: scientificNotationToString(v)
                                }
                            })
                        }}
                        onChangeOpen={onChangeOpen}
                    />
                )
            }
            case 'date': {
                const dateValue = variableValue && isDateValue(variableValue) ? variableValue : ''
                return field ? (
                    <DateInput
                        size={size}
                        field={field}
                        data={dateValue}
                        placeholder={placeholder}
                        onChange={onChange}
                        onChangeOpen={onChangeOpen}
                    />
                ) : null
            }
            case 'checkbox': {
                return (
                    <SC.ItemContainer size={size}>
                        {variableValue === undefined ? (
                            <Placeholder style={{ color: 'var(--color-gray-400)' }} value={placeholder || '请选择'} />
                        ) : (
                            <Flex style={{ width: '100%', paddingRight: 2 }} justifyContent="space-between">
                                <CheckboxItemPreviewer
                                    id={id}
                                    data={Boolean(variableValue)}
                                    onChange={e =>
                                        onChange({
                                            type: VariableType.VALUE,
                                            valueVariable: { type: fieldType, value: e.currentTarget.checked }
                                        })
                                    }
                                    disabled={disabled}
                                    defaultBgcolor="transparent"
                                />
                                <SC.Icon
                                    type="CloseCircle"
                                    size={16}
                                    isAction
                                    onClick={e => {
                                        e.stopPropagation()
                                        !disabled && onChange(DEFAULT_VALUE_VARIABLE)
                                    }}
                                />
                            </Flex>
                        )}

                        <Tooltip title="选择数据">
                            <TagIcon
                                icon="DotsThreeVertical"
                                size={24}
                                radius={5}
                                isActive={opened}
                                iconSize={16}
                                enableHover
                                hoverBackground="rgba(38, 65, 90, 0.06)"
                                onClick={() => onChangeOpen?.()}
                            />
                        </Tooltip>
                    </SC.ItemContainer>
                )
            }
            case 'video':
            case 'file': {
                const fileUrls = variableValue === undefined ? variableValue : safeToArray(variableValue)
                const defaultPlaceholder = fieldType === 'file' ? '粘贴文件链接' : ' 粘贴视频链接'
                return (
                    <FileInput
                        size={size}
                        disabled={disabled}
                        field={field}
                        data={fileUrls}
                        placeholder={placeholder ?? defaultPlaceholder}
                        opened={opened}
                        filePercent={filePercent}
                        onChange={onChange}
                        onChangeOpen={onChangeOpen}
                        onCancelUpload={onCancelUpload}
                    />
                )
            }

            default: {
                return (
                    <SelectInput
                        size={size}
                        field={field}
                        data={data}
                        placeholder={placeholder}
                        onChange={onChange}
                        onChangeOpen={onChangeOpen}
                    />
                )
            }
        }
    }, [data, disabled, field, fieldType, fieldTypeRef, filePercent, focusOutLine, id, onCancelUpload, onChange, onChangeOpen, opened, placeholder, prefix, size, width])
}
